import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import api from "../lib/api";
import { ShieldCheck, Send, Sparkles, ArrowRight, ChevronDown } from "lucide-react";

const HERO_VIDEO = "https://assets.mixkit.co/videos/preview/mixkit-aerial-shot-of-a-vineyard-32798-large.mp4";
const HERO_POSTER = "https://images.unsplash.com/photo-1629424511021-1be28bf5ae1e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80";

export default function Home() {
    const [wineries, setWineries] = useState([]);

    useEffect(() => {
        api.get("/wineries?featured=true").then(({ data }) => setWineries(data)).catch(() => setWineries([]));
    }, []);

    return (
        <Layout transparentNav>
            {/* HERO */}
            <section className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={HERO_POSTER}
                    className="absolute inset-0 w-full h-full object-cover"
                >
                    <source src={HERO_VIDEO} type="video/mp4" />
                </video>
                {/* [F1] Contrast — deeper overlay for premium legibility */}
                <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.2) 100%)" }} />
                {/* [F1] Soft vignette from the bottom for editorial depth */}
                <div className="absolute inset-x-0 bottom-0 h-56 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0))" }} />

                <div className="relative z-10 h-full flex items-center px-6 md:px-12 lg:px-24">
                    <div className="max-w-3xl">
                        <p className="overline hero-fade-up">Premium Wine · Direct Trade</p>
                        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl mt-6 leading-[0.95] hero-fade-up" style={{ animationDelay: "120ms" }}>
                            Direct from<br />the World&apos;s<br />
                            <em className="font-display-italic text-[#C9A961]">Finest Cellars.</em>
                        </h1>
                        <p className="mt-8 text-lg md:text-xl text-[#E4E4E7] max-w-xl leading-relaxed hero-fade-up" style={{ animationDelay: "240ms" }}>
                            Verified premium wineries. No middlemen. Global import, curated.
                        </p>
                        <div className="flex flex-wrap items-center gap-6 mt-12 hero-fade-up" style={{ animationDelay: "360ms" }}>
                            <Link to="/wineries" className="btn-gold hero-cta-glow !px-10 !py-5 !text-base" data-testid="hero-explore-wineries">
                                Explore Wineries <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/wines" className="btn-ghost text-sm tracking-[0.15em] uppercase border-b border-[#27272A] hover:border-[#C9A961] pb-1 transition-colors duration-500" data-testid="hero-browse-wines">
                                Browse the catalogue
                            </Link>
                        </div>
                        <p className="mt-8 text-xs tracking-[0.18em] uppercase text-[#A1A1AA] hero-fade-up" style={{ animationDelay: "480ms" }} data-testid="hero-trust-line">
                            Trusted by premium importers in 30+ countries
                        </p>
                    </div>
                </div>

                {/* [F1] Elegant scroll cue */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#C9A961] hero-scroll-cue pointer-events-none" aria-hidden="true">
                    <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
                    <ChevronDown className="w-4 h-4" strokeWidth={1.25} />
                </div>
            </section>

            {/* FEATURED WINERIES */}
            <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24" data-testid="featured-wineries-section">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
                    <div>
                        <p className="overline">The Houses</p>
                        <h2 className="font-display text-4xl md:text-6xl mt-3">Featured Wineries</h2>
                    </div>
                    <Link to="/wineries" className="text-sm tracking-[0.15em] uppercase hover:text-[#C9A961] transition-colors duration-500 border-b border-[#27272A] hover:border-[#C9A961] pb-1 w-fit">
                        View all →
                    </Link>
                </div>

                {wineries.length === 0 ? (
                    <div className="border border-[#27272A] p-16 text-center" data-testid="no-wineries-message">
                        <p className="font-display text-3xl text-[#A1A1AA]">Curating Argentina&apos;s finest...</p>
                        <p className="text-sm text-[#71717A] mt-4">Approved wineries will appear here. Are you a producer?</p>
                        <Link to="/register" className="btn-outline mt-8 inline-flex">Join CellarGlobal</Link>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
                        {wineries.map((w) => (
                            <Link key={w.id} to={`/winery/${w.slug}`} className="group block hero-winery-card" data-testid={`featured-winery-${w.slug}`}>
                                <div className="aspect-[4/5] overflow-hidden bg-[#0A0A0A]">
                                    <img
                                        src={w.banner_url || w.logo_url || "https://images.unsplash.com/photo-1767969217452-3cc1eb722703?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"}
                                        alt={w.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="mt-6">
                                    <p className="overline">{w.region}</p>
                                    <h3 className="font-display text-3xl mt-2 group-hover:text-[#C9A961] transition-colors duration-500">{w.name}</h3>
                                    {w.established_year && (
                                        <p className="text-sm text-[#71717A] mt-1">Est. {w.established_year}</p>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* WHY CELLARGLOBAL */}
            <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24 border-t border-[#27272A]" data-testid="why-section">
                <div className="max-w-3xl mb-20">
                    <p className="overline">The Philosophy</p>
                    <h2 className="font-display text-4xl md:text-6xl mt-3">Why CellarGlobal</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-12 md:gap-16">
                    {[
                        { icon: ShieldCheck, title: "Verified", body: "Every winery on our platform is hand-verified by our Argentine team. No imposters, no markups, no surprises." },
                        { icon: Send, title: "Direct", body: "Speak directly with the cellar master. Quotes flow without intermediaries — your price is the producer's price." },
                        { icon: Sparkles, title: "Premium", body: "Curated for restaurants, distributors and collectors who refuse to compromise on provenance or quality." },
                    ].map(({ icon: Icon, title, body }, i) => (
                        <div key={title} className="border-t border-[#27272A] pt-8" data-testid={`why-${title.toLowerCase()}`}>
                            {/* [F1] Discreet editorial index */}
                            <p className="text-[10px] tracking-[0.35em] uppercase text-[#C9A961] mb-6">{String(i + 1).padStart(2, "0")}</p>
                            <Icon className="w-8 h-8 text-[#C9A961]" strokeWidth={1.25} />
                            <h3 className="font-display text-3xl mt-6">{title}</h3>
                            <p className="text-base text-[#A1A1AA] mt-4 leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center" data-testid="cta-section">
                <p className="overline">For Producers</p>
                <h2 className="font-display text-4xl md:text-6xl mt-3 max-w-3xl mx-auto leading-tight">
                    Reach importers in <em className="font-display-italic text-[#C9A961]">42 countries.</em>
                </h2>
                <Link to="/register" className="btn-gold hero-cta-glow mt-12 inline-flex" data-testid="cta-list-winery">
                    List Your Winery <ArrowRight className="w-4 h-4" />
                </Link>
            </section>
        </Layout>
    );
}
